/**
 * F43 (Track B) — Central report queue.
 *
 * Route: /reports
 * Permission: reports:read
 *
 * Table over the UNION of `user_reports` ∪ F32 legacy `open_game_reports`
 * (the read-mapper normalizes the legacy vocabulary + attaches the synthetic
 * `subjectType:'open_game'`). Status + reason + subject filters, row-selection +
 * bulk reassign/dismiss (gated `reports:resolve` → BulkActionsDrawer), and a
 * releasable claim affordance. Clicking a row navigates to /reports/:id.
 *
 * Data source: the F43 `reportsApi` (aliased to its fixture in tests).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  FormField,
  Select,
  Skeleton,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import {
  REPORT_REASONS,
  REPORT_STATUSES,
  REPORT_SUBJECT_TYPES,
  type AdminPageMeta,
  type ReportListItem,
} from '@bravemove/types';
import { reportsApi, type AssignableAdmin, type ReportListQuery } from '../../api/reports';
import { useAuth } from '../../auth/useAuth';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';
import { BulkActionsDrawer, type BulkKind } from '../../components/moderation/BulkActionsDrawer';

const PAGE_LIMIT = 25;

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  open: 'warning',
  under_review: 'info',
  actioned: 'success',
  dismissed: 'neutral',
};

function buildQuery(raw: {
  subjectType: string | null;
  status: string | null;
  reason: string | null;
  page: number;
}): ReportListQuery {
  return {
    perPage: PAGE_LIMIT,
    page: raw.page,
    ...(raw.subjectType ? { subjectType: raw.subjectType } : {}),
    ...(raw.status ? { status: raw.status as never } : {}),
    ...(raw.reason ? { reason: raw.reason as never } : {}),
  };
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminReportsQueuePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const canResolve = auth.adminUser?.permissions?.includes('reports:resolve') ?? false;

  const subjectParam = searchParams.get('subjectType') ?? '';
  const statusParam = searchParams.get('status') ?? '';
  const reasonParam = searchParams.get('reason') ?? '';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly ReportListItem[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkKind, setBulkKind] = useState<BulkKind | null>(null);
  // PU-02 — the assignable admin-staff list for bulk reassign. Empty when the
  // actor lacks `admin_users:read` (the client degrades a 403 to []) — the bulk
  // Reassign button is then hidden instead of opening a permanently-disabled form.
  const [assignableAdmins, setAssignableAdmins] = useState<readonly AssignableAdmin[]>([]);

  const fetchList = useCallback(
    async (opts: ReportListQuery) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const res = await reportsApi.list(opts);
        setRows(res.data);
        setPagination(res.meta.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  const currentQuery = (): ReportListQuery =>
    buildQuery({
      subjectType: subjectParam || null,
      status: statusParam || null,
      reason: reasonParam || null,
      page: pageParam,
    });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(currentQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectParam, statusParam, reasonParam, pageParam]);

  // PU-02 — fetch the admin-staff list once (resolvers only; shared with the
  // BulkActionsDrawer). A 403/network failure resolves to [].
  useEffect(() => {
    if (!canResolve) return;
    let cancelled = false;
    void reportsApi.listAssignableAdmins().then((admins) => {
      if (!cancelled) setAssignableAdmins(admins);
    });
    return () => {
      cancelled = true;
    };
  }, [canResolve]);

  function setParam(key: string, value: string) {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  }

  function goToPage(page: number) {
    setSelectedIds(new Set());
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  }

  const subjectOptions = useMemo(
    () => [
      { value: '', label: t('moderation:queue.filter.all') },
      ...REPORT_SUBJECT_TYPES.map((s) => ({
        value: s,
        label: t(`moderation:report.subject_type.${s}`),
      })),
      { value: 'open_game', label: t('moderation:report.subject_type.open_game') },
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('moderation:queue.filter.all') },
      ...REPORT_STATUSES.map((s) => ({ value: s, label: t(`moderation:report.status.${s}`) })),
    ],
    [t],
  );

  const reasonOptions = useMemo(
    () => [
      { value: '', label: t('moderation:queue.filter.all') },
      ...REPORT_REASONS.map((r) => ({ value: r, label: t(`moderation:report.reason.${r}`) })),
    ],
    [t],
  );

  const columns = useMemo<readonly TableColumn<ReportListItem>[]>(
    () => [
      {
        key: 'subject',
        header: t('moderation:queue.column.subject'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[13px] text-ink">
              {t(`moderation:report.subject_type.${row.subjectType}`)}
            </span>
            <span className="font-mono text-[11px] text-ink-3 truncate">{row.subjectId}</span>
          </div>
        ),
      },
      {
        key: 'reason',
        header: t('moderation:queue.column.reason'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {t(`moderation:report.reason.${row.reason}`)}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('moderation:queue.column.status'),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge tone={STATUS_TONES[row.status] ?? 'neutral'} size="sm">
              {t(`moderation:report.status.${row.status}`)}
            </Badge>
            {row.isLegacyOpenGame ? (
              <Badge tone="info" size="sm" data-testid={`f43-report-legacy-${row.id}`}>
                {t('moderation:queue.legacy_open_game')}
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: 'assignee',
        header: t('moderation:queue.column.assignee'),
        cell: (row) => <span className="text-[12px] text-ink-3">{row.assignedAdminId ?? '—'}</span>,
      },
      {
        key: 'filed',
        header: t('moderation:queue.column.filed'),
        cell: (row) => (
          <span className="text-[12px] text-ink-3">{formatDate(row.createdAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  // open-game legacy rows are read-only in the central queue → only select
  // native rows for bulk reassign/dismiss.
  const bulkSelectableRows = useMemo(() => rows.filter((r) => !r.isLegacyOpenGame), [rows]);

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <PageHeader title={t('moderation:queue.title')} subtitle={t('moderation:queue.subtitle')} />

      {/* Filters */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FormField label={t('moderation:queue.filter.subject_type')}>
          <Select
            value={subjectParam}
            onChange={(e) => setParam('subjectType', e.target.value)}
            options={subjectOptions}
            data-testid="f43-reports-filter-subject"
          />
        </FormField>
        <FormField label={t('moderation:queue.filter.status')}>
          <Select
            value={statusParam}
            onChange={(e) => setParam('status', e.target.value)}
            options={statusOptions}
            data-testid="f43-reports-filter-status"
          />
        </FormField>
        <FormField label={t('moderation:queue.filter.reason')}>
          <Select
            value={reasonParam}
            onChange={(e) => setParam('reason', e.target.value)}
            options={reasonOptions}
            data-testid="f43-reports-filter-reason"
          />
        </FormField>
      </div>

      {/* Bulk toolbar */}
      {canResolve && selectedIds.size > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-outline bg-surface-2 px-3 py-2"
          data-testid="f43-reports-bulk-toolbar"
        >
          <span className="text-[13px] font-medium text-ink">
            {t('admin:f43.bulk.selected', { count: selectedIds.size })}
          </span>
          <div className="flex flex-wrap gap-2 ms-auto">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setBulkKind('dismiss-reports')}
              data-testid="f43-reports-bulk-dismiss"
            >
              {t('moderation:bulk.dismiss_reports')}
            </Button>
            {assignableAdmins.length > 0 ? (
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setBulkKind('reassign-reports')}
                data-testid="f43-reports-bulk-reassign"
              >
                {t('moderation:bulk.reassign_reports')}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setSelectedIds(new Set())}
              data-testid="f43-reports-bulk-clear"
            >
              {t('admin:f43.bulk.clear')}
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="flag" size={36} color="currentColor" />}
          title={t('moderation:queue.empty')}
        />
      ) : (
        <Table
          data={rows as ReportListItem[]}
          columns={columns as TableColumn<ReportListItem>[]}
          rowKey={(row) => row.id}
          {...(canResolve
            ? {
                selectable: true,
                selectedIds,
                onSelectionChange: (ids: ReadonlySet<string>) => {
                  // Never let a legacy open-game row enter the bulk selection.
                  const native = new Set(
                    [...ids].filter((id) => bulkSelectableRows.some((r) => r.id === id)),
                  );
                  setSelectedIds(native);
                },
              }
            : {})}
          onRowPress={(row) =>
            // PU-03 — legacy F32 open-game rows have no native detail (the
            // detail route reads only user_reports → guaranteed 404 banner).
            // Route them to the F32 surface instead; `subjectId` is the
            // open-game id, whose admin detail carries the report history.
            navigate(row.isLegacyOpenGame ? `/open-games/${row.subjectId}` : `/reports/${row.id}`)
          }
          empty={t('moderation:queue.empty')}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="reports-pagination"
        />
      ) : null}

      {bulkKind !== null ? (
        <BulkActionsDrawer
          open
          kind={bulkKind}
          targetIds={[...selectedIds]}
          assignableAdmins={assignableAdmins}
          onClose={() => setBulkKind(null)}
          onDone={() => {
            setBulkKind(null);
            setSelectedIds(new Set());
            void fetchList(currentQuery());
          }}
        />
      ) : null}
    </div>
  );
}
